"use client";

import { useState, useTransition, useCallback } from "react";
import { castLCKVoteAction, undoLCKVoteAction, getLCKVoters } from "../actions/lck";
import { useRouter } from "next/navigation";
import HoldToUndo from "./hold-to-undo";
import { clog } from "@/lib/client-log";

interface Voter {
  userId: string;
  user: { displayName: string | null; role: string };
}

interface Props {
  nameId: string;
  nameName: string;
  alreadyVoted: boolean;
  /** Total votes cast so far (for the info panel vote count) */
  currentVoteCount?: number;
  /** Whether this name has been resurrected (blocks undo) */
  resurrectedAlready?: boolean;
  /**
   * "vote"     — cast the vote directly (used on /lck page only)
   * "navigate" — just link to /lck (used on /names, /bracket teaser areas)
   */
  mode?: "vote" | "navigate";
}

export default function LCKVoteButton({
  nameId,
  nameName,
  alreadyVoted,
  currentVoteCount,
  resurrectedAlready = false,
  mode = "vote",
}: Props) {
  const [voted, setVoted] = useState(alreadyVoted);
  const [resurrected, setResurrected] = useState(false);
  const [undone, setUndone] = useState(false);
  const [voters, setVoters] = useState<Voter[] | null>(null);
  const [voterPanelOpen, setVoterPanelOpen] = useState(false);
  const [voterLoading, setVoterLoading] = useState(false);
  const [isPending, startTransition] = useTransition();
  const router = useRouter();

  // Undo is allowed if voted, not yet resurrected, and undo hasn't happened
  const canUndo = voted && !resurrected && !resurrectedAlready && !undone;

  // ── Navigate mode: just send user to LCK page ───────────────────────────────
  if (mode === "navigate") {
    return (
      <a
        href="/lck"
        title={`Vote to bring back ${nameName} in Last Chance Kitchen`}
        className="flex flex-col items-center gap-1 rounded-xl bg-amber-400 hover:bg-amber-300 active:scale-95 px-3 py-2 transition min-w-[56px] text-center select-none"
        style={{ WebkitUserSelect: "none", userSelect: "none" }}
      >
        <div className="text-2xl">⭐</div>
        <div className="text-xs font-black text-amber-900 whitespace-nowrap">LCK →</div>
      </a>
    );
  }

  // ── Vote mode ────────────────────────────────────────────────────────────────
  function handleVote() {
    if (voted || isPending) return;
    clog.info("lck-vote", "casting LCK vote", { nameId, nameName });
    startTransition(async () => {
      try {
        const result = await castLCKVoteAction(nameId);
        if (result.success) {
          clog.info("lck-vote", "LCK vote cast", { nameId, resurrected: result.resurrected });
          setVoted(true);
          if (result.resurrected) {
            setResurrected(true);
            setTimeout(() => router.refresh(), 1200);
          }
          if (voterPanelOpen) loadVoters();
        } else {
          clog.warn("lck-vote", `castLCKVoteAction rejected: ${result.error}`, { nameId });
        }
      } catch (err) {
        clog.error("lck-vote", "castLCKVoteAction threw", {
          error: err instanceof Error ? err.message : String(err),
        });
      }
    });
  }

  const loadVoters = useCallback(async () => {
    setVoterLoading(true);
    try {
      const data = await getLCKVoters(nameId);
      setVoters(data);
      clog.debug("lck-vote", "voters loaded", { nameId, count: data.length });
    } catch (err) {
      clog.error("lck-vote", "getLCKVoters failed", {
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setVoterLoading(false);
    }
  }, [nameId]);

  const toggleVoterPanel = useCallback(async () => {
    const next = !voterPanelOpen;
    setVoterPanelOpen(next);
    if (next && !voters) {
      await loadVoters();
    }
  }, [voterPanelOpen, voters, loadVoters]);

  const handleUndo = useCallback(() => {
    clog.info("lck-vote", "undoing LCK vote", { nameId });
    startTransition(async () => {
      try {
        const result = await undoLCKVoteAction(nameId);
        if (result.success) {
          clog.info("lck-vote", "LCK vote undone", { nameId });
          setVoted(false);
          setUndone(true);
          setVoterPanelOpen(false);
          setVoters(null);
        } else {
          clog.warn("lck-vote", `undoLCKVoteAction rejected: ${result.error}`, { nameId });
        }
      } catch (err) {
        clog.error("lck-vote", "undoLCKVoteAction threw", {
          error: err instanceof Error ? err.message : String(err),
        });
      }
    });
  }, [nameId]);

  // ── Resurrected state ────────────────────────────────────────────────────────
  if (resurrected) {
    return (
      <div className="text-center flex flex-col items-center gap-1">
        <div className="text-3xl animate-bounce">🔥</div>
        <div className="text-xs font-black text-orange-600 uppercase tracking-widest">Back!</div>
      </div>
    );
  }

  // ── Undone state (briefly shown before page revalidates) ────────────────────
  if (undone) {
    return (
      <div className="flex flex-col items-center gap-1 min-w-[56px]">
        <div className="text-xl opacity-40">⭐</div>
        <div className="text-xs text-gray-400">Vote undone</div>
      </div>
    );
  }

  // ── Voted state — show voter info toggle + hold-to-undo ─────────────────────
  if (voted) {
    return (
      <div className="flex flex-col items-center gap-1.5 min-w-[72px]">
        {/* Voter info toggle button */}
        <button
          onClick={toggleVoterPanel}
          title="Tap to see who voted"
          className="flex flex-col items-center gap-0.5 group select-none"
          style={{ WebkitUserSelect: "none", userSelect: "none" }}
        >
          <div className="text-xl">🤍</div>
          <div className="text-[10px] text-purple-400 font-bold group-hover:text-purple-300 transition">
            {voterPanelOpen ? "hide" : `${voters ? voters.length : (currentVoteCount ?? "?")} voted`}
          </div>
        </button>

        {/* Voter panel */}
        {voterPanelOpen && (
          <div className="absolute z-10 mt-1 w-44 rounded-xl bg-white shadow-xl border border-gray-100 p-2 text-xs">
            {voterLoading ? (
              <div className="text-gray-400 text-center py-2 animate-pulse">Loading...</div>
            ) : voters && voters.length > 0 ? (
              <div className="flex flex-col gap-1">
                {voters.map((v) => (
                  <div key={v.userId} className="flex items-center gap-1.5">
                    <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center text-[8px] font-black text-purple-600 shrink-0">
                      {(v.user.displayName ?? "?").slice(0, 2).toUpperCase()}
                    </div>
                    <span className="text-gray-700 truncate">{v.user.displayName ?? "Unknown"}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-400 text-center py-1">No votes yet</div>
            )}
          </div>
        )}

        {/* Hold-to-undo ring — only while resurrection hasn't happened */}
        {canUndo && (
          <HoldToUndo
            label="Hold 3 seconds to undo your LCK vote"
            onUndo={handleUndo}
            disabled={isPending}
            size={56}
            colorClass="stroke-amber-400"
          />
        )}
      </div>
    );
  }

  // ── Default: rescue button ───────────────────────────────────────────────────
  return (
    <button
      onClick={handleVote}
      disabled={isPending}
      title={`Vote to bring back ${nameName}`}
      className="flex flex-col items-center gap-1 rounded-xl bg-amber-400 hover:bg-amber-300 active:scale-95 px-3 py-2 transition disabled:opacity-50 min-w-[56px] select-none"
      style={{ WebkitUserSelect: "none", userSelect: "none" }}
    >
      <div className="text-2xl">⭐</div>
      <div className="text-xs font-black text-amber-900">
        {isPending ? "…" : "Rescue"}
      </div>
    </button>
  );
}
